import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useFileUploadRouting, submitInstitutionRequest } from './uploadRouting';

// Stage machine:
//   idle → reading → matched (parser found) | unmatched (no reader) | error
//   unmatched → requested (user asked us to build a reader)
export function useInstitutionImport(onClose) {
  const [stage, setStage] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [unmatched, setUnmatched] = useState(null);
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [requestSaving, setRequestSaving] = useState(false);

  const onMatched = useCallback((body) => { setPreview(body.preview); setStage('matched'); }, []);
  const onUnmatched = useCallback((body) => {
    setUnmatched({
      ...body,
      editableName: body.institution_guess || '',
      recognised: !!body.recognised,
    });
    setStage('unmatched');
  }, []);
  const onError = useCallback(() => { setStage('idle'); }, []);

  const {
    files, fileInputRef, routeAndUpload, reset: resetFiles,
  } = useFileUploadRouting({ onMatched, onUnmatched, onError, setStage });

  const resetAll = useCallback(() => {
    setStage('idle'); setPreview(null); setUnmatched(null);
    setConfirmSaving(false); setRequestSaving(false); resetFiles();
  }, [resetFiles]);

  const handleClose = useCallback(() => { resetAll(); onClose?.(); }, [onClose, resetAll]);

  const handleRequestReader = useCallback(async () => {
    if (!unmatched?.editableName?.trim()) {
      toast.error('Please tell us the institution name first.'); return;
    }
    setRequestSaving(true);
    try {
      const { ok, body } = await submitInstitutionRequest({
        institutionName: unmatched.editableName,
        countryCode: unmatched.user_country || 'ZZ',
        sourceFormat: unmatched.source_format || 'csv',
        notes: `Files: ${files.map((f) => f.name).join(', ')}`,
      });
      if (!ok) {
        toast.error(body?.detail || "We couldn't record your request. Please try again."); return;
      }
      toast.success(body.message || 'Thanks — we\'ve noted your institution.');
      setStage('requested');
    } finally { setRequestSaving(false); }
  }, [unmatched, files]);

  const updateEditableName = useCallback((name) => {
    setUnmatched((u) => (u ? { ...u, editableName: name } : u));
  }, []);

  return {
    stage, files, file: files[0] || null, preview, unmatched,
    confirmSaving, requestSaving, fileInputRef,
    resetAll, handleClose,
    handleFileSelected: routeAndUpload,   // accepts a single File or a FileList
    handleRequestReader, updateEditableName, setConfirmSaving,
  };
}
